import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  buildCaptions,
  findCaptionAtSeconds,
  findWordIndexAtSeconds,
  CAPTION_REVEAL_MODE,
} from '@vbee-holding/aivoice-shared-lib';
import { drawCaption } from '@vbee-holding/video-renderer/draw';
import { resolveCaptionStyle } from '@vbee-holding/video-renderer/schema';
import { CAPTION_STYLE_NONE } from '@src/constants/dubbing';
import { BLOCK_STATUS } from '@src/constants/project';
import { computeCanvasPixelSize } from '@src/services/project';
import { getMeasuredAudioDuration } from '@src/services/audioDuration';
import useFontCatalog from '@src/hooks/useFontCatalog';
import useKaraokeEnabled from '@src/hooks/useKaraokeEnabled';
import { resolveFaceUrl, loadCaptionFont } from '@src/services/caption';
import useCaptionTemplates from '../useCaptionTemplates';
import useBlockAudioDurations from '../useBlockAudioDurations';
import { StyledCaptionOverlayCanvas } from './index.style';

/**
 * Draws the current block's caption with the engine the export burns into the
 * mp4, so the two match by construction. The video stays a plain <video>.
 */
const CaptionOverlay = ({ isPlaying, getCurrentSec, currentSec }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  // Redraw guard: the rAF loop only paints when the block changes.
  const lastCaptionRef = useRef(null);
  const lastWordRef = useRef(-1);

  // Selected here rather than passed down, so caption data changes re-render
  // this small layer instead of the whole editor tree.
  const blocks = useSelector((state) => state.project.multiBlock?.blocks);
  const blockIds = useSelector((state) => state.project.multiBlock?.blockIds);
  const translationStatus = useSelector(
    (state) => state.project.translation?.status,
  );
  const synthesisParams = useSelector((state) => state.project.synthesisParams);
  const karaokeEnabled = useKaraokeEnabled();

  // Bumps once the audio lengths are known, to re-estimate with them.
  const durationsVersion = useBlockAudioDurations(
    blocks,
    blockIds,
    karaokeEnabled,
  );
  // The export calls this same function, which is what keeps the two in step.
  const captionTimeline = useMemo(
    () =>
      buildCaptions(
        blockIds?.map((blockId) => blocks?.[blockId]),
        {
          translationStatus,
          withWordTimings: karaokeEnabled,
          // An edited block still has the old audio, so it would time old text.
          getAudioDuration: (block) =>
            block?.blockStatus === BLOCK_STATUS.AUDIO_AVAILABLE
              ? getMeasuredAudioDuration(block.audioLink)
              : undefined,
          pauses: synthesisParams?.clientPause,
          dictionary: synthesisParams?.pronunciationDict,
        },
      ),
    [
      blocks,
      blockIds,
      translationStatus,
      synthesisParams,
      karaokeEnabled,
      durationsVersion,
    ],
  );

  const captionStyle = useSelector(
    (state) => state.project.videoSettings?.captionStyle,
  );
  const hasCaption =
    Boolean(captionStyle?.styleId) &&
    captionStyle.styleId !== CAPTION_STYLE_NONE;
  const templates = useCaptionTemplates();
  const config = useMemo(
    () =>
      captionStyle?.config ??
      templates.find(({ code }) => code === captionStyle?.styleId)?.config,
    [captionStyle, templates],
  );

  // The API returns a sparse config, so resolve once — drawCaption runs per frame.
  const style = useMemo(() => resolveCaptionStyle(config).style, [config]);

  const fonts = useFontCatalog();
  const faceUrl = resolveFaceUrl(fonts, {
    ...style.text,
    font: config?.text?.font,
  });

  const [fontsReady, setFontsReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setFontsReady(false);
    loadCaptionFont(style.text, faceUrl).then(() => {
      if (!cancelled) setFontsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [style, faceUrl]);

  // These two repaint per word, not per block.
  const perWord = useMemo(
    () =>
      Boolean(
        style.highlight?.enabled ||
          style.layout?.revealMode === CAPTION_REVEAL_MODE.BY_SPEECH,
      ),
    [style],
  );

  const drawOverlay = useCallback(
    (force = false) => {
      const canvas = canvasRef.current;
      if (!canvas || !canvas.width) return;
      const seconds = getCurrentSec ? getCurrentSec() : 0;
      const caption =
        hasCaption && fontsReady
          ? findCaptionAtSeconds(captionTimeline, seconds)
          : null;
      const wordIndex =
        perWord && caption
          ? findWordIndexAtSeconds(caption.words, seconds)
          : -1;
      // One object per block, so comparing the reference is enough.
      if (
        !force &&
        caption === lastCaptionRef.current &&
        wordIndex === lastWordRef.current
      )
        return;
      lastCaptionRef.current = caption;
      lastWordRef.current = wordIndex;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (caption) {
        drawCaption(ctx, {
          caption,
          style,
          time: seconds,
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
        });
      }
    },
    [captionTimeline, hasCaption, style, perWord, fontsReady, getCurrentSec],
  );

  // `drawOverlay` changes identity on every keystroke, so the loop reads it
  // through a ref — as a dep it would rebuild the loop on each character.
  const drawRef = useRef(drawOverlay);
  useEffect(() => {
    drawRef.current = drawOverlay;
  });

  // A resize clears the canvas, so always repaint after it.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const size = computeCanvasPixelSize(
        entry.contentRect.width,
        entry.contentRect.height,
        window.devicePixelRatio,
      );
      if (canvas.width !== size.width || canvas.height !== size.height) {
        canvas.width = size.width;
        canvas.height = size.height;
      }
      drawRef.current(true);
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  // One rAF loop for the whole playback, skipping unchanged frames.
  useEffect(() => {
    if (!isPlaying) return undefined;
    drawRef.current(true);
    const loop = () => {
      drawRef.current();
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isPlaying]);

  // Repaint on a data, style or seek change. Null while playing so `timeupdate`
  // ticks do not force repaints the rAF loop already covers.
  const pausedSec = isPlaying ? null : currentSec;
  useEffect(() => {
    drawOverlay(true);
  }, [drawOverlay, pausedSec]);

  return <StyledCaptionOverlayCanvas ref={canvasRef} />;
};

export default memo(CaptionOverlay);
